import { MinimalClientCompany } from '../../bookingOrders/types/ClientCompany';
import ContactPerson from '../../bookingOrders/types/ContactPerson';
import Course from '../../course/types/Course';
import { MinimalUser } from '../../user/types/User';
import { MinimalSubsidiaryCompany } from '../../subsidiaryCompany/types/SubsidiaryCompany';
import {
  CertificateLanguage,
  CertificateTemplate,
  CertificateTemplateType,
} from '../../certificate/types/Certificate';

export type OpenClassCertificateType = `${CertificateTemplateType}`;

// Derived by the backend. `Completed` isn't a value, it's how the UI shows a past confirmed
// class. See isOpenClassPast.
export enum OpenClassStatus {
  Canceled = 'canceled',
  Preliminary = 'preliminary',
  Confirmed = 'confirmed',
  Ordered = 'ordered',
}
export type CertificateResultStatus = 'not_recorded' | 'passed' | 'failed';
export type AttendanceStatus = 'not_recorded' | 'present' | 'not_present';

type Timestamped = {
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
};

// What a class tells us about a certificate it offers. The full template lives behind the
// certificate template endpoints, which not every role can read.
export type OpenClassCertificateOffering = {
  id: CertificateTemplate['id'];
  languages: CertificateLanguage[];
  type: OpenClassCertificateType;
};

export type OpenClass = Timestamped & {
  id: number;
  course: Course;
  status: OpenClassStatus;
  startAt: string;
  endAt: string;
  certificateTemplates: OpenClassCertificateOffering[];
  sessions: OpenClassSession[];
};

export type OpenClassSession = Timestamped & {
  id: number;
  openClassId: OpenClass['id'];
  startAt: string;
  endAt: string;
  capacity: number;
  assignedSeatCount?: number;
  remainingCapacity?: number;
  instructors: MinimalUser[];
  location?: string | null;
  lat?: number | null;
  lng?: number | null;
  comment?: string | null;
  status: OpenClassStatus;
};

export type OpenClassOrderContact = {
  name: string;
  email: string;
  phoneNumber?: string | null;
};

export type OpenClassBookingOrder = Timestamped & {
  id: number;
  clientCompany: MinimalClientCompany | null;
  contactPerson: ContactPerson | null;
  contact?: OpenClassOrderContact | null;
  subsidiaryCompany: MinimalSubsidiaryCompany;
  orderDate: string;
  comment?: string | null;
};

export type Attendee = Timestamped & {
  id: number;
  clientCompany: MinimalClientCompany | null;
  name: string;
  email?: string | null;
  // ISO YYYY-MM-DD. Required by the backend for a furbo certificate registration.
  birthDate?: string | null;
  birthPlace?: string | null;
  subsidiaryCompany?: MinimalSubsidiaryCompany | null;
};

export type OpenClassRegistration = Timestamped & {
  id: number;
  openClassId: OpenClass['id'];
  bookingOrderId: OpenClassBookingOrder['id'];
  orderParticipantId?: number;
  attendee: Attendee;
  certificateTemplateId: CertificateTemplate['id'];
  certificateType: OpenClassCertificateType;
  certificateResultStatus: CertificateResultStatus;
  attestNumber?: string | null;
  besaccCertificateFileName?: string | null;
  certificateSentAt?: string | null;
  certificateSentTo?: string | null;
  // Only the class registrations listing fills this in
  orderHasContact?: boolean | null;
  sessionAttendances?: OpenClassSessionAttendance[];
};

export type OpenClassBookingOrderParticipant = Timestamped & {
  id: number;
  bookingOrderId: OpenClassBookingOrder['id'];
  attendee: Attendee;
  registrations: OpenClassRegistration[];
};

export type OpenClassOrderCompleteness = 'complete' | 'incomplete';

export type OpenClassBookingOrderDetails = {
  order: OpenClassBookingOrder;
  participants: OpenClassBookingOrderParticipant[];
  completeness: OpenClassOrderCompleteness;
};

export type OpenClassOrderClassSummary = {
  openClassId: number;
  courseId: number;
  label: string;
};

export type OpenClassBookingOrderSummary = {
  order: OpenClassBookingOrder;
  participantCount: number;
  unassignedParticipantCount: number;
  completeness: OpenClassOrderCompleteness;
  classes: OpenClassOrderClassSummary[];
};

export type OpenClassOrderConflictCode =
  | 'absentParticipant'
  | 'birthDetailsRequired'
  | 'capacityConflict'
  | 'certificateIssued'
  | 'certificateNotEarned'
  | 'certificateNotOffered'
  | 'companyLocked'
  | 'duplicateAttendee'
  | 'duplicateParticipant'
  | 'duplicateRegistration'
  | 'invalidRequest'
  | 'openClassCompleted'
  | 'overlappingSession'
  | 'resourceNotFound'
  | 'resultsRecorded'
  | 'sessionMismatch'
  | 'subsidiaryMismatch';

export type OpenClassOrderConflict = {
  code: OpenClassOrderConflictCode;
  attendeeId?: number;
  participantId?: number;
  openClassId?: number;
  sessionId?: number;
  remainingCapacity?: number;
};

export type OpenClassSessionAttendance = Timestamped & {
  id: number;
  openClassId: OpenClass['id'];
  sessionId: OpenClassSession['id'];
  registrationId: OpenClassRegistration['id'];
  attendanceStatus: AttendanceStatus;
  result: CertificateResultStatus;
  comment?: string | null;
};

export type CreateOpenClassParams = {
  courseId: Course['id'];
  certificateTemplateIds: number[];
  sessions: CreateOpenClassSessionParams[];
};

export type UpdateOpenClassParams = {
  courseId: Course['id'];
  certificateTemplateIds: number[];
};

// The backend rejects `canceled: true` at create, so two shapes make that unrepresentable.
export type CreateOpenClassSessionParams = {
  startAt: string;
  endAt: string;
  capacity: number;
  instructorIds: number[];
  location?: string | null;
  lat?: number | null;
  lng?: number | null;
  comment?: string | null;
  confirmed?: boolean;
};

export type UpdateOpenClassSessionParams = CreateOpenClassSessionParams & {
  canceled?: boolean;
};

// Advisory soft overruns for live feedback. Hard conflicts come back as a 409 on save.
export type ValidateOpenClassSessionParams = {
  instructorIds: number[];
  startAt: string;
  endAt: string;
};

export type CreateOpenClassBookingOrderParams = {
  clientCompanyId: MinimalClientCompany['id'];
  contactPersonId?: ContactPerson['id'] | null;
  orderDate: string;
  comment?: string | null;
};

export type UpdateOpenClassBookingOrderParams = {
  clientCompanyId?: MinimalClientCompany['id'];
  contactPersonId?: ContactPerson['id'] | null;
  contact?: OpenClassOrderContact;
  orderDate: string;
  comment?: string | null;
};

export type CreateStandaloneOpenClassBookingOrderParams = {
  subsidiaryCompanyId: number;
  contact: OpenClassOrderContact;
  orderDate: string;
  comment?: string | null;
};

export type AddOpenClassOrderParticipantParams = {
  attendee: NewAttendeeParams;
};

export type NewAttendeeParams = {
  name: string;
  email?: string | null;
  birthDate?: string | null;
  birthPlace?: string | null;
};

export type OpenClassRegistrationSelectionParams = {
  openClassId: OpenClass['id'];
  certificateTemplateId: CertificateTemplate['id'];
  sessionIds: number[];
};

export type UpdateCertificateResultParams = {
  certificateResultStatus: CertificateResultStatus;
};

export type UpdateSessionAttendanceParams = {
  attendanceStatus: AttendanceStatus;
  comment?: string | null;
  result?: CertificateResultStatus;
};
